import { useState, useEffect } from 'react';

/**
 * Media query hook with SSR default to avoid hydration mismatch
 * Uses window.matchMedia to detect viewport width - no User-Agent parsing
 * Defaults to desktop (false) on SSR, client-side detects actual viewport
 */
export const useMediaQuery = (query: string, ssrDefault: boolean = false): boolean => {
  const isServer = typeof window === 'undefined';
  const [matches, setMatches] = useState<boolean>(() => {
    if (isServer) return ssrDefault;
    try {
      return window.matchMedia(query).matches;
    } catch {
      return ssrDefault;
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const media = window.matchMedia(query);
    const listener = () => setMatches(media.matches);
    try {
      media.addEventListener ? media.addEventListener('change', listener) : media.addListener(listener as any);
      setMatches(media.matches);
      return () => {
        media.removeEventListener ? media.removeEventListener('change', listener) : media.removeListener(listener as any);
      };
    } catch {
      return;
    }
  }, [query]);

  return matches;
};
